import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

interface PageHeaderProps {
  title: string;
  description?: string;
  actions?: ReactNode;
  filters?: ReactNode;
  className?: string;
}

export function PageHeader({
  title,
  description,
  actions,
  filters,
  className,
}: PageHeaderProps): React.ReactElement {
  // shrink-0: pages mount this inside a column-flex <main> whose height is
  // capped by the scroll container, so flex items may shrink. min-h-[54px]
  // replaces the default `min-height: auto` floor, letting the header be
  // squeezed to 54px when filters + actions stack taller — the actions row
  // then overlaps the content below.
  return (
    <div
      className={cn(
        "flex min-h-[54px] shrink-0 flex-wrap items-start justify-between gap-x-6 gap-y-3",
        className,
      )}
    >
      <div className="flex min-w-0 flex-1 basis-[180px] flex-col gap-[3px]">
        <h1 className="truncate text-[25px] font-bold leading-[30px] tracking-[-0.02em] text-foreground">
          {title}
        </h1>
        {description ? (
          <p className="text-xs leading-[18px] text-muted-foreground">{description}</p>
        ) : null}
      </div>
      {filters || actions ? (
        <div className="flex max-w-full flex-col items-end gap-3 pt-px">
          {filters}
          {actions ? (
            <div className="flex max-w-full flex-wrap items-center gap-2">{actions}</div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
